import {FC, useEffect, useMemo} from 'react';
import {useLocation, useRoute} from 'wouter';

import {PRODUCT_NAME} from '@shared/constants';
import {IS_RUNNING_LOCALLY} from '@shared/env';
import {removeUndefined} from '@shared/lib/utils/type_utils';

import {notifyError} from '@shared_web/lib/notification';

import {HOME_FULL_REGEX, SEARCH_FULL_REGEX} from '@mavismart_frontend/components/app';
import {
  findTranslationKey,
  translate,
  TranslateKey,
} from '@mavismart_frontend/components/translate';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {useDoor} from '@mavismart_frontend/stores/all_doors_store';
import {useGroups} from '@mavismart_frontend/stores/groups_store';

export const TabTitle: FC = () => {
  const [location] = useLocation();
  const account = useAccount();
  const lang = getUserLanguage(account);
  const [, params1] = useRoute(HOME_FULL_REGEX);
  const [, params2] = useRoute(SEARCH_FULL_REGEX);
  const {groupId, doorId, category} = {...params1, ...params2};

  // Get group label
  const allGroups = useGroups();
  const groupLabel = groupId
    ? allGroups
      ? (allGroups.find(group => group.group.id === groupId)?.group.name ?? groupId)
      : groupId
    : undefined;

  // Get door label
  const doorLabel = useDoor(doorId)?.label;

  const title = useMemo(() => {
    //
    // Home - Groups - Doors - Search
    //
    if (location === '/') {
      return PRODUCT_NAME;
    }
    const isGroups = location.startsWith('/groups');
    const isDoors = location.startsWith('/doors');
    const isSearch = location.startsWith('/search');
    if (isGroups || isDoors || isSearch) {
      const base: string = (() => {
        if (doorLabel && groupLabel) {
          return `${doorLabel} (${groupLabel})`;
        }
        if (doorLabel && !groupLabel) {
          return doorLabel;
        }
        if (!doorLabel && groupLabel) {
          return groupLabel;
        }
        // no groupId and no doorId
        const key: TranslateKey = isGroups ? 'groups' : isDoors ? 'doors' : 'search';
        return translate(key, lang);
      })();

      const suffixes: string[] = (() => {
        if (location.includes('/messages')) {
          const translateKey = category ? findTranslationKey(category) : undefined;
          const suffix1 = translateKey ? translate(translateKey, lang) : undefined;
          const suffix2 = location.includes('/send') ? 'Send' : undefined;
          return removeUndefined([translate('messages', lang), suffix1, suffix2]);
        }
        if (location.includes('/doors') && !location.includes('/doors/')) {
          return [translate('doors', lang)];
        }
        if (location.includes('/info')) {
          return [translate('info', lang)];
        }
        if (location.includes('/statistics')) {
          return [translate('statistics', lang)];
        }
        if (location.includes('/sharing')) {
          return [translate('sharing', lang)];
        }
        return [];
      })();

      return [base, suffixes.join(' - ')].join(' ');
    }

    //
    // SAV
    //
    if (location.startsWith('/support')) {
      return translate('sav', lang);
    }

    //
    // Support Messages
    //
    if (location.startsWith('/support/messages')) {
      return translate('support_messages_title', lang);
    }

    //
    // User Notifications & Account
    //
    if (location.startsWith('/user/account')) {
      return translate('user_account', lang);
    }
    if (location.startsWith('/user/notifications')) {
      return translate('notifications', lang);
    }
    if (location.includes('/user/settings')) {
      return translate('settings', lang);
    }

    //
    // Other pages
    //
    if (location.startsWith('/terms')) {
      return translate('terms', lang);
    }
    if (location.startsWith('/login')) {
      return translate('login', lang);
    }
    if (location.startsWith('/account-verification')) {
      return translate('verify_account', lang);
    }

    //
    // Tracking
    //
    if (location.startsWith('/tracking')) {
      return location.includes('/journey')
        ? `Parcours utilisateur - ${PRODUCT_NAME}`
        : `Tracking - ${PRODUCT_NAME}`;
    }

    // If we arrive here it means there is a case we haven't handled.
    // We log an error and show a notification error if we are in dev.
    const err = new Error(`No title for location: ${location}`);
    if (IS_RUNNING_LOCALLY) {
      notifyError(err);
    } else {
      console.error(err);
    }
    return PRODUCT_NAME;
  }, [location, doorLabel, groupLabel, category, lang]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return <></>;
};

TabTitle.displayName = 'TabTitle';
